import { AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useState } from 'react';

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info';
    itemDetails?: React.ReactNode;
    requireTextConfirmation?: boolean;
    confirmationText?: string;
}

const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'warning',
    itemDetails,
    requireTextConfirmation = false,
    confirmationText = 'delete my account'
}: ConfirmationModalProps) => {
    const [inputValue, setInputValue] = useState('');

    const handleConfirm = () => {
        onConfirm();
        onClose();
        setInputValue('');
    };

    const handleClose = () => {
        onClose();
        setInputValue('');
    };

    const isConfirmDisabled = requireTextConfirmation && inputValue.toLowerCase() !== confirmationText.toLowerCase();

    const getVariantColors = () => {
        switch (variant) {
            case 'danger':
                return {
                    iconBg: 'bg-red-500/10',
                    iconColor: 'text-red-400',
                    borderColor: 'border-red-500/20',
                    buttonBg: 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600',
                };
            case 'warning':
                return {
                    iconBg: 'bg-yellow-500/10',
                    iconColor: 'text-yellow-400',
                    borderColor: 'border-yellow-500/20',
                    buttonBg: 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500',
                };
            case 'info':
                return {
                    iconBg: 'bg-blue-500/10',
                    iconColor: 'text-blue-400',
                    borderColor: 'border-blue-500/20',
                    buttonBg: 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500',
                };
        }
    };

    const colors = getVariantColors();

    if (!isOpen) return null;

    return createPortal(
        
            <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
                {/* Backdrop */}
                <div
                    onClick={handleClose}
                    className="absolute inset-0 bg-black/60 backdrop-blur-md"
                />

                {/* Modal */}
                <div
                    className="relative bg-cosmic-900/40 backdrop-blur-2xl rounded-2xl md:rounded-3xl border border-white/20 max-w-md w-full shadow-2xl z-[100]"
                >
                    {/* Header */}
                    <div className="flex items-start gap-3 p-4 md:p-6 border-b border-white/10">
                        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl ${colors.iconBg} flex items-center justify-center shrink-0`}>
                            <AlertTriangle size={20} className={`md:w-6 md:h-6 ${colors.iconColor}`} />
                        </div>
                        <div className="flex-1 pt-1">
                            <h2 className="text-lg md:text-xl font-bold text-white font-serif leading-tight">
                                {title}
                            </h2>
                            {itemDetails && (
                                <div className="mt-1.5 flex items-center gap-2">
                                    <div className={`w-1.5 h-1.5 rounded-full ${variant === 'danger' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' : variant === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'}`}></div>
                                    <p className="text-sm font-medium text-purple-100 font-sans line-clamp-1">
                                        {itemDetails}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 md:p-6 pb-2 md:pb-3">
                        <p className="text-sm md:text-base text-purple-200/80 leading-relaxed font-sans">
                            {message}
                        </p>

                        {requireTextConfirmation && (
                            <div className="mt-4">
                                <label className={`block text-xs font-bold uppercase tracking-wider mb-2 font-sans ${variant === 'danger' ? 'text-red-300' : variant === 'warning' ? 'text-yellow-300' : 'text-blue-300'}`}>
                                    Type "{confirmationText}" to confirm
                                </label>
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={confirmationText}
                                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none font-sans transition-all ${
                                        variant === 'danger' ? 'border-red-500/30 focus:border-red-500' :
                                        variant === 'warning' ? 'border-yellow-500/30 focus:border-yellow-500' :
                                        'border-blue-500/30 focus:border-blue-500'
                                    }`}
                                    autoFocus
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 md:p-6 border-t border-white/10 flex flex-col-reverse sm:flex-row gap-3">
                        <button
                            onClick={handleClose}
                            className="flex-1 py-3 md:py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold rounded-xl md:rounded-2xl transition-all text-sm md:text-base"
                        >
                            {cancelText}
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={isConfirmDisabled}
                            className={`flex-1 py-3 md:py-3.5 ${colors.buttonBg} text-white font-bold rounded-xl md:rounded-2xl transition-all shadow-lg text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none`}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        ,
        document.body
    );
};

export default ConfirmationModal;
